import { Injectable, NgZone } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { AlertController, Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import {
  AppUpdate,
  AppUpdateAvailability,
} from "@capawesome/capacitor-app-update";
import { firstValueFrom } from "rxjs";
import { filter, timeout } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ApiService } from "./api.service";

interface PlatformVersionConfig {
  latestVersion: string;
  storeUrl?: string;
}

interface AppVersionConfig {
  android?: PlatformVersionConfig;
  ios?: PlatformVersionConfig;
  // Backend switch: true -> mandatory update, otherwise optional
  isPublish?: boolean;
}

/**
 * Prompts users to update to the latest app version.
 *
 * Reads the version config from the API (ApiService.getAppVersionConfig) on launch and resume.
 * When installed < latestVersion:
 * - isPublish true  -> blocking update popup (cannot be dismissed)
 * - isPublish false -> "new version available" prompt, dismissible with "Later", which hides it
 *   until a newer latestVersion is published
 *
 * Neither is shown while the user is filling a form or doing a sign-off; they are
 * deferred until the user navigates away from those screens.
 * If the config can't be fetched (offline, error), the user is let in.
 */
@Injectable({
  providedIn: "root",
})
export class AppUpdateService {
  // Routes where the update screen must not interrupt the user (forms, sign-offs, check-in flows)
  private readonly DEFERRED_ROUTE_PATTERNS = [
    "/form-",
    "/management-form-relaunch",
    "/signoff-",
    "/image-annotation",
    "/checkin-induction",
    "/checkin-workpermit",
    "/checkinout-",
    "/permits-generate-dm",
    "/jspdfviewer",
    "/document-openchoice",
  ];

  private readonly FETCH_TIMEOUT_MS = 10000;
  private readonly DISMISSED_VERSION_STORAGE_KEY = "app_update_dismissed_version";

  private initialized = false;
  private checking = false;
  private pendingAction: "force" | "soft" | null = null;
  private platformConfig: PlatformVersionConfig = null;
  private forceUpdateAlert: HTMLIonAlertElement = null;
  private softUpdateAlert: HTMLIonAlertElement = null;

  constructor(
    private platform: Platform,
    private router: Router,
    private ngZone: NgZone,
    private alertController: AlertController,
    private translateService: TranslateService,
    private apiService: ApiService
  ) {}

  init() {
    if (
      this.initialized ||
      !Capacitor.isNativePlatform() ||
      environment.isFormPreview
    ) {
      return;
    }
    this.initialized = true;

    this.checkForUpdate();

    this.platform.resume.subscribe(() => {
      this.checkForUpdate();
    });

    // Show a deferred update screen once the user leaves the form / sign-off
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.pendingAction && !this.isOnDeferredRoute()) {
          const action = this.pendingAction;
          this.pendingAction = null;
          this.applyAction(action);
        }
      });
  }

  async checkForUpdate() {
    if (this.checking) {
      return;
    }
    this.checking = true;
    try {
      const config = await this.fetchConfig();
      const platformName = Capacitor.getPlatform();
      const platformConfig: PlatformVersionConfig =
        platformName === "ios" ? config?.ios : config?.android;
      if (!platformConfig?.latestVersion) {
        return;
      }
      this.platformConfig = platformConfig;

      const { version: installedVersion } = await App.getInfo();

      let action: "force" | "soft" | null = null;
      if (this.compareVersions(installedVersion, platformConfig.latestVersion) < 0) {
        if (config.isPublish === true) {
          action = "force";
        } else if (!this.isDismissed(platformConfig.latestVersion)) {
          action = "soft";
        }
      }

      if (action !== "force") {
        // isPublish may have been turned off since the last check
        await this.closeForceUpdateAlert();
      }
      if (action !== "soft") {
        // isPublish turned on, or the app was updated, since the last check
        await this.closeSoftUpdateAlert();
      }

      if (!action) {
        this.pendingAction = null;
        return;
      }

      if (this.isOnDeferredRoute()) {
        this.pendingAction = action;
      } else {
        this.pendingAction = null;
        await this.applyAction(action);
      }
    } catch (error) {
      // Fail open: never block the user because the config couldn't be read
      console.warn("[APP UPDATE] Version check skipped:", error);
    } finally {
      this.checking = false;
    }
  }

  /** Opens Google Play's in-app update flow (Android) or the App Store page (iOS). */
  async openStore() {
    try {
      if (Capacitor.getPlatform() === "android") {
        const info = await AppUpdate.getAppUpdateInfo();
        if (
          info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE &&
          info.immediateUpdateAllowed
        ) {
          await AppUpdate.performImmediateUpdate();
          return;
        }
        await AppUpdate.openAppStore();
      } else {
        const appId = this.getIosAppId(this.platformConfig?.storeUrl);
        await AppUpdate.openAppStore(appId ? { appId } : undefined);
      }
    } catch (error) {
      console.warn("[APP UPDATE] Could not open store via plugin:", error);
      if (this.platformConfig?.storeUrl) {
        window.open(this.platformConfig.storeUrl, "_system");
      }
    }
  }

  private async applyAction(action: "force" | "soft") {
    if (action === "force") {
      await this.showForceUpdateAlert();
    } else {
      await this.showSoftUpdatePrompt();
    }
  }

  private fetchConfig(): Promise<AppVersionConfig> {
    return firstValueFrom(
      this.apiService
        .getAppVersionConfig()
        .pipe(timeout(this.FETCH_TIMEOUT_MS))
    );
  }

  private async showForceUpdateAlert() {
    if (this.forceUpdateAlert) {
      return;
    }
    await this.ngZone.run(async () => {
      this.forceUpdateAlert = await this.alertController.create({
        header: this.translateService.instant(
          "SHARED_TEXT.APP_UPDATE.REQUIRED_TITLE"
        ),
        message: this.translateService.instant(
          "SHARED_TEXT.APP_UPDATE.REQUIRED_MESSAGE"
        ),
        backdropDismiss: false,
        buttons: [
          {
            text: this.translateService.instant(
              "SHARED_TEXT.APP_UPDATE.UPDATE_NOW"
            ),
            handler: () => {
              this.openStore();
              // Keep the popup open: the user must update to continue
              return false;
            },
          },
        ],
      });
      this.forceUpdateAlert.onDidDismiss().then(() => {
        this.forceUpdateAlert = null;
      });
      await this.forceUpdateAlert.present();
    });
  }

  private async closeForceUpdateAlert() {
    if (!this.forceUpdateAlert) {
      return;
    }
    const alert = this.forceUpdateAlert;
    this.forceUpdateAlert = null;
    await alert.dismiss().catch(() => {});
  }

  private async showSoftUpdatePrompt() {
    // Already on screen (e.g. resumed without tapping "Later"): don't stack a second one
    if (!this.platformConfig?.latestVersion || this.softUpdateAlert) {
      return;
    }
    await this.ngZone.run(async () => {
      this.softUpdateAlert = await this.alertController.create({
        header: this.translateService.instant(
          "SHARED_TEXT.APP_UPDATE.SOFT_TITLE"
        ),
        message: this.translateService.instant(
          "SHARED_TEXT.APP_UPDATE.SOFT_MESSAGE"
        ),
        // Only "Later" closes it, not a tap outside
        backdropDismiss: false,
        buttons: [
          {
            text: this.translateService.instant("SHARED_TEXT.APP_UPDATE.LATER"),
            role: "cancel",
            handler: () => {
              this.dismissVersion(this.platformConfig.latestVersion);
            },
          },
          {
            text: this.translateService.instant(
              "SHARED_TEXT.APP_UPDATE.UPDATE_NOW"
            ),
            handler: () => {
              this.openStore();
            },
          },
        ],
      });
      this.softUpdateAlert.onDidDismiss().then(() => {
        this.softUpdateAlert = null;
      });
      await this.softUpdateAlert.present();
    });
  }

  private async closeSoftUpdateAlert() {
    if (!this.softUpdateAlert) {
      return;
    }
    const alert = this.softUpdateAlert;
    this.softUpdateAlert = null;
    await alert.dismiss().catch(() => {});
  }

  /** "Later" hides the optional popup for that latestVersion, until a newer one is published. */
  private dismissVersion(latestVersion: string) {
    try {
      localStorage.setItem(this.DISMISSED_VERSION_STORAGE_KEY, latestVersion);
    } catch (error) {}
  }

  private isDismissed(latestVersion: string): boolean {
    try {
      return localStorage.getItem(this.DISMISSED_VERSION_STORAGE_KEY) === latestVersion;
    } catch (error) {
      return false;
    }
  }

  private isOnDeferredRoute(): boolean {
    const url = this.router.url || "";
    return this.DEFERRED_ROUTE_PATTERNS.some((pattern) => url.includes(pattern));
  }

  private getIosAppId(storeUrl: string): string {
    const match = /id(\d+)/.exec(storeUrl || "");
    return match ? match[1] : null;
  }

  /** Compares "major.minor.patch" versions. Returns <0 if a < b, 0 if equal, >0 if a > b. */
  private compareVersions(a: string, b: string): number {
    const partsA = (a || "0").split(".").map((part) => parseInt(part, 10) || 0);
    const partsB = (b || "0").split(".").map((part) => parseInt(part, 10) || 0);
    const length = Math.max(partsA.length, partsB.length);
    for (let i = 0; i < length; i++) {
      const diff = (partsA[i] || 0) - (partsB[i] || 0);
      if (diff !== 0) {
        return diff;
      }
    }
    return 0;
  }
}
